export const SERVICE_WORKER_UPDATE_INTERVAL_MS = 5 * 60 * 1000;
export const SERVICE_WORKER_UPDATE_READY_EVENT = 'valentine:service-worker-update-ready';

let serviceWorkerUpdateReady = false;
const updateReadyListeners = new Set<() => void>();

function markServiceWorkerUpdateReady() {
  if (serviceWorkerUpdateReady) return;
  serviceWorkerUpdateReady = true;
  updateReadyListeners.forEach((listener) => listener());
}

// Keep the event as the integration boundary while retaining a snapshot for
// React consumers that mount after the replacement worker has taken control.
if (typeof window !== 'undefined') {
  window.addEventListener(SERVICE_WORKER_UPDATE_READY_EVENT, markServiceWorkerUpdateReady);
}

export function getServiceWorkerUpdateReady(): boolean {
  return serviceWorkerUpdateReady;
}

export function subscribeServiceWorkerUpdateReady(listener: () => void): () => void {
  updateReadyListeners.add(listener);
  return () => updateReadyListeners.delete(listener);
}

type WindowTarget = Pick<Window, 'addEventListener' | 'removeEventListener' | 'dispatchEvent' | 'setInterval' | 'clearInterval' | 'location'>;
type DocumentTarget = Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;

export interface ServiceWorkerUpdateOptions {
  serviceWorker?: ServiceWorkerContainer;
  /** The registration created for this app surface, never a broader scope. */
  registration?: ServiceWorkerRegistration;
  windowTarget?: WindowTarget;
  documentTarget?: DocumentTarget;
  intervalMs?: number;
}

/**
 * Checks the worker promptly after startup and whenever a staffer returns to
 * Valentine. Browser soft-update checks can otherwise lag behind a deployment,
 * leaving an old precached app shell on screen for too long.
 */
export function installServiceWorkerUpdates(options: ServiceWorkerUpdateOptions = {}): () => void {
  const serviceWorker = options.serviceWorker
    ?? (typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : undefined);
  const windowTarget = options.windowTarget ?? (typeof window !== 'undefined' ? window : undefined);
  const documentTarget = options.documentTarget ?? (typeof document !== 'undefined' ? document : undefined);

  if (!serviceWorker || !windowTarget || !documentTarget) return () => {};

  let previousController = serviceWorker.controller;
  let refreshing = false;
  let disposed = false;
  let hiddenReloadListener: (() => void) | undefined;
  let activeRegistration: ServiceWorkerRegistration | undefined;
  let updateFoundListener: (() => void) | undefined;
  const workerStateListeners: Array<{ worker: ServiceWorker; listener: () => void }> = [];

  const requestUpdate = () => {
    const registrationPromise = options.registration
      ? Promise.resolve(options.registration)
      : serviceWorker.getRegistration();
    void registrationPromise
      .then((registration) => registration?.update())
      // An update check is opportunistic; a network hiccup must never affect
      // the running app or create a user-facing error.
      .catch((error: unknown) => console.warn('Valentine service worker update check failed:', error));
  };

  const skipWaiting = (worker: ServiceWorker | null) => {
    if (!disposed && worker?.state === 'installed' && serviceWorker.controller) {
      worker.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  const watchInstalling = (worker: ServiceWorker | null) => {
    if (!worker || disposed) return;
    skipWaiting(worker);
    const listener = () => skipWaiting(worker);
    worker.addEventListener('statechange', listener);
    workerStateListeners.push({ worker, listener });
  };

  const readyRegistration = options.registration
    ? Promise.resolve(options.registration)
    : serviceWorker.ready;
  void readyRegistration.then((registration) => {
    if (disposed) return;
    activeRegistration = registration;
    skipWaiting(registration.waiting);
    // registerSW.js can begin an update before this module finishes MSAL
    // bootstrap, so observe a worker that is already installing as well as
    // workers announced by later updatefound events.
    watchInstalling(registration.installing);
    updateFoundListener = () => {
      watchInstalling(registration.installing);
    };
    registration.addEventListener('updatefound', updateFoundListener);
  });

  const reloadWhenHidden = () => {
    if (refreshing || hiddenReloadListener) return;

    const reload = () => {
      if (refreshing || disposed) return;
      refreshing = true;
      windowTarget.location.reload();
    };

    if (documentTarget.visibilityState === 'hidden') {
      reload();
      return;
    }

    hiddenReloadListener = () => {
      if (documentTarget.visibilityState !== 'hidden') return;
      documentTarget.removeEventListener('visibilitychange', hiddenReloadListener!);
      hiddenReloadListener = undefined;
      reload();
    };
    documentTarget.addEventListener('visibilitychange', hiddenReloadListener);
  };

  const onControllerChange = () => {
    const nextController = serviceWorker.controller;
    const isReplacement = previousController !== null
      && nextController !== null
      && nextController !== previousController;
    previousController = nextController;
    // A first install taking control, an unchanged controller, or removal of a
    // registration is not a deploy update and must not prompt or reload.
    if (!isReplacement) return;
    markServiceWorkerUpdateReady();
    windowTarget.dispatchEvent(new Event(SERVICE_WORKER_UPDATE_READY_EVENT));
    reloadWhenHidden();
  };
  const onFocus = () => requestUpdate();
  const onVisibilityChange = () => {
    if (documentTarget.visibilityState === 'visible') requestUpdate();
  };

  serviceWorker.addEventListener('controllerchange', onControllerChange);
  windowTarget.addEventListener('focus', onFocus);
  documentTarget.addEventListener('visibilitychange', onVisibilityChange);
  const intervalId = windowTarget.setInterval(requestUpdate, options.intervalMs ?? SERVICE_WORKER_UPDATE_INTERVAL_MS);
  requestUpdate();

  return () => {
    disposed = true;
    serviceWorker.removeEventListener('controllerchange', onControllerChange);
    windowTarget.removeEventListener('focus', onFocus);
    documentTarget.removeEventListener('visibilitychange', onVisibilityChange);
    if (hiddenReloadListener) {
      documentTarget.removeEventListener('visibilitychange', hiddenReloadListener);
    }
    if (activeRegistration && updateFoundListener) {
      activeRegistration.removeEventListener('updatefound', updateFoundListener);
    }
    workerStateListeners.forEach(({ worker, listener }) => worker.removeEventListener('statechange', listener));
    windowTarget.clearInterval(intervalId);
  };
}
